import { isValid, parse } from 'date-fns'
import { isNumber, isString } from 'lodash-es'
import {
  ColumnMapping,
  ColumnMappingDecimal,
  ColumnMappingEnum,
  ColumnMappingLocalDate,
  ColumnMappingString,
  DateFormat,
  DateTerm,
  Direction,
  OrderTerm,
  Severity,
  ValidationIssue,
  ValidationType
} from 'api'
import { compareInsensitive, Decimal, ErrorResult, localToUTC, notEmpty, NullResult, Result, ValueResult } from 'utils'
import { BASIS_POINTS_DECIMAL_SHIFT, DataHelper, DateTermHelper, getDateEditorFormat, getEnumValues, PERCENTAGE_DECIMAL_SHIFT } from 'model'
import { DeriveDirection } from '../DeriveDirection'
import { parseCell } from './parseCell'
import { parseNumber } from './parseNumber'

export interface SpreadsheetParseResult<TRowData = any> {
  filename: string
  rows: TRowData[]
  issues: ValidationIssue[]
}

export interface TypedSpreadsheetParseResult<T, D = any> extends SpreadsheetParseResult<D> {
  parsedItems: T[]
}

export class SpreadsheetParseError extends Error {
  constructor(message: string, public readonly rowNumber: number, public readonly fieldName: string) {
    super(message)
  }
}

export function gatherIssues(...results: Result<any>[]): ValidationIssue[] {
  return results.reduce((issues, result) => {
    if (result.isError) {
      issues.push({
        errorMessage: result.error.message,
        fieldName: result.error instanceof SpreadsheetParseError ? result.error.fieldName || '' : result.fieldName,
        severity: Severity.ERROR,
        type: ValidationType.INVALID_VALUE
      })
    }
    return issues
  }, [] as ValidationIssue[])
}

export function parseString(
  row: any,
  rowNum: number,
  columnMapping: ColumnMappingString,
  fieldName: string,
  mandatory: boolean = false
): Result<string> {
  return parseCell(row, rowNum, columnMapping, fieldName, mandatory).map(cellValue => {
    if (isString(cellValue)) {
      return notEmpty(cellValue) ? cellValue.trim() : null
    } else {
      return String(cellValue)
    }
  })
}

export function parseStringToArray(
  row: any,
  rowNum: number,
  columnMapping: ColumnMappingString,
  fieldName: string,
  mandatory: boolean = false
): Result<string[]> {
  return parseCell(row, rowNum, columnMapping, fieldName, mandatory).map(cellValue =>
    isString(cellValue)
      ? notEmpty(cellValue)
        ? cellValue.split(',').map(splitCellValue => splitCellValue.trim())
        : null
      : String(cellValue)
          .split(',')
          .map(splitCellValue => splitCellValue.trim())
  )
}

export function parseDecimal(
  row: any,
  rowNum: number,
  columnMapping: ColumnMappingDecimal,
  fieldName: string,
  mandatory: boolean = false
): Result<number> {
  return parseNumber(row, rowNum, columnMapping, fieldName, mandatory).map(numValue =>
    Decimal.shift(numValue, columnMapping.decimalShift || 0)
  )
}

export function parseRate(
  row: any,
  rowNum: number,
  columnMapping: ColumnMapping,
  fieldName: string,
  refRateResult: Result<string>,
  mandatory: boolean = false
): Result<number> {
  return parseNumber(row, rowNum, columnMapping, fieldName, mandatory).map(numValue => {
    const decimalShift = refRateResult.isValue ? BASIS_POINTS_DECIMAL_SHIFT : PERCENTAGE_DECIMAL_SHIFT
    return Decimal.shift(numValue, decimalShift || 0)
  })
}

export function excelDateToDate(excelDate: number) {
  // Days between 1/1/1900 and 1/1/1970   = 25,567
  // Seconds in a day                     = 86,400
  // Excel specifies the number of days since 1/1/1900 but treats this as day 1 rather than 0 so we subtract 1 for that
  // Excel treats 1900 as a leap year so we subtract 1 for that
  return new Date((excelDate - 25567 - 1 - 1) * 86400 * 1000)
}

function parseWithCentury(dateString: string, formatString: string, referenceDate: Date) {
  let parsedDate = parse(dateString, formatString, referenceDate)
  const year = parsedDate.getFullYear()

  if (year >= 0 && year < 1000) {
    parsedDate.setFullYear(year + 2000)
  }

  return parsedDate
}

export function parseDateValue(value: any, dateFormat: DateFormat | null) {
  if (isNumber(value)) {
    return excelDateToDate(value)
  } else if (isString(value)) {
    if (!dateFormat) {
      throw new Error(`Please select a date format for "${value}"`)
    }
    const formatString = getDateEditorFormat(dateFormat)
    const localDate = parseWithCentury(value, formatString, new Date())

    if (isValid(localDate)) {
      return localToUTC(localDate)
    } else {
      throw new Error(`"${value}" is not a valid date for format ${dateFormat}`)
    }
  } else {
    throw new Error(`"${value}" is not a valid date`)
  }
}

export function parseDate(
  row: any,
  rowNum: number,
  columnMapping: ColumnMappingLocalDate,
  fieldName: string,
  mandatory: boolean = false
): Result<Date> {
  return parseCell(row, rowNum, columnMapping, fieldName, mandatory).map(cellValue => {
    try {
      return parseDateValue(cellValue, columnMapping.dateFormat)
    } catch (err) {
      throw new SpreadsheetParseError(err instanceof Error ? err.message : '', rowNum, fieldName)
    }
  })
}

export function getEnumMappings(Enum: any, dataHelper: DataHelper<any>): any {
  return getEnumValues(Enum).reduce((mappings, enumValue) => {
    mappings[enumValue] = dataHelper.getName(enumValue)
    return mappings
  }, {})
}

export function parseEnum<T>(
  row: any,
  rowNum: number,
  columnMapping: ColumnMapping,
  enumValues: { [key: string]: string },
  fieldName: string,
  ignoreWhitespace: boolean = false,
  mandatory: boolean = false
): Result<T> {
  const result = parseString(row, rowNum, columnMapping, fieldName, mandatory).map(strValue => {
    const matched = Object.keys(enumValues).find(key => compareInsensitive(enumValues[key], strValue, ignoreWhitespace) === 0)

    if (matched) {
      return (matched as unknown) as T
    } else {
      throw new SpreadsheetParseError(`"${strValue}" is not a valid option`, rowNum, fieldName)
    }
  })

  return result
}

export function parseEnumArray<T>(
  row: any,
  rowNum: number,
  columnMapping: ColumnMapping,
  enumValues: { [key: string]: string },
  fieldName: string,
  ignoreWhitespace: boolean = false,
  mandatory: boolean = false
): Result<T[]> {
  const result = parseString(row, rowNum, columnMapping, fieldName, mandatory).map(strValue =>
    strValue.split(',').map(value => {
      const matched = Object.keys(enumValues).find(key => compareInsensitive(enumValues[key], value.trim(), ignoreWhitespace) === 0)
      if (matched) {
        return (matched as unknown) as T
      } else {
        throw new SpreadsheetParseError(`"${strValue}" is not a valid option`, rowNum, fieldName)
      }
    })
  )

  return result
}

export function parseDirectionWithNotionalAndStartConsideration(
  row: any,
  rowNum: number,
  columnMapping: ColumnMappingEnum<Direction>,
  fieldName: string,
  notionalResult: Result<number>,
  startConsiderationResult: Result<number>,
  mandatory: boolean = false
): Result<Direction> {
  if (columnMapping.heading !== DeriveDirection.PositiveIsBuy && columnMapping.heading !== DeriveDirection.PositiveIsSell) {
    return parseDirection(row, rowNum, columnMapping, fieldName, mandatory)
  }

  if (!notionalResult.isValue && !startConsiderationResult.isValue) {
    return NullResult
  }

  if (notionalResult.isValue && startConsiderationResult.isValue && notionalResult.value < 0 !== startConsiderationResult.value < 0) {
    return new ErrorResult(
      new SpreadsheetParseError(
        `Notional "${notionalResult.value}" and start consideration "${startConsiderationResult.value}" must have the same sign.`,
        rowNum,
        fieldName
      )
    )
  }

  if (columnMapping.heading === DeriveDirection.PositiveIsBuy) {
    if (notionalResult.isValue) {
      return notionalResult.value < 0 ? new ValueResult(Direction.SELL) : new ValueResult(Direction.BUY)
    } else {
      return startConsiderationResult.value < 0 ? new ValueResult(Direction.SELL) : new ValueResult(Direction.BUY)
    }
  } else {
    if (notionalResult.isValue) {
      return notionalResult.value < 0 ? new ValueResult(Direction.BUY) : new ValueResult(Direction.SELL)
    } else {
      return startConsiderationResult.value < 0 ? new ValueResult(Direction.BUY) : new ValueResult(Direction.SELL)
    }
  }
}

export function parseDirection(
  row: any,
  rowNum: number,
  columnMapping: ColumnMappingEnum<Direction>,
  fieldName: string,
  mandatory: boolean
): Result<Direction> {
  return parseEnum<Direction>(row, rowNum, columnMapping, columnMapping.values, fieldName, true, mandatory)
}

export function parseDateAndDateTerm(
  row: any,
  rowNum: number,
  columnMapping: ColumnMappingLocalDate,
  fieldName: string,
  mandatory: boolean = false,
  spreadsheetTerm: Result<OrderTerm>
): Result<Date> | Result<DateTerm> {
  const startDateTerm = parseEnum<DateTerm>(row, rowNum, columnMapping, getEnumMappings(DateTerm, DateTermHelper), fieldName, true)

  if (spreadsheetTerm.isValue && spreadsheetTerm.value !== OrderTerm.CUSTOM && spreadsheetTerm.value !== OrderTerm.CUSTOM_O) {
    return NullResult
  }

  if (startDateTerm.isValue) {
    return startDateTerm
  }

  return parseDate(row, rowNum, columnMapping, fieldName, mandatory)
}

export const convertNumericStringValuesToNumber = (input: string) => {
  const convertedValue = Number(input)
  return isNaN(convertedValue) ? input : convertedValue
}

export const removeAllSpacesFromString = (input: string) => {
  return input.replace(/ /g, '')
}
